import {getLocalStorageItem, setLocalStorageItem} from '@gaia/utils';
import {useState} from 'react';
import useSWRImmutable from 'swr/immutable';

const planIdentifiers = ['GaiaGPSPremiumStripe2018', 'O+DigitalStripe'] as const;

export type PlanIdentifier = (typeof planIdentifiers)[number];

const apiBase = '/api/subscriptions/plans/';

export interface SubscriptionPlan {
    identifier: PlanIdentifier;
    amount: number;
    discounted_amount: number | null;
    coupon_string: string | null;
    coupon_code: string | null;
}

const fetcher = (api: string): Promise<SubscriptionPlan> =>
    fetch(api)
        .then((res) => res.json())
        .then((plans) => plans.at(0));

export function useSubscriptionPlan(identifier: PlanIdentifier) {
    const key = `subscription_plan_${identifier}`;

    const [fallbackData] = useState<SubscriptionPlan | undefined>(() =>
        getLocalStorageItem<SubscriptionPlan | undefined>(key, undefined)
    );

    const {data: plan} = useSWRImmutable(
        `${apiBase}?${new URLSearchParams({identifier})}`,
        fetcher,
        {
            onSuccess(data) {
                setLocalStorageItem(key, data);
            },
        }
    );

    return plan ?? fallbackData;
}

export function useSubscriptionPlanDiscountedAmount(identifier: PlanIdentifier) {
    // -Infinity as sentinel value for "not found"
    const plan = useSubscriptionPlan(identifier);
    if (plan === undefined) return -Infinity;
    if (plan.discounted_amount !== null) return plan.discounted_amount;
    return plan.amount;
}
